import { ColumnConfig } from "@shared/components/GridV2/SnGridTypes";

/**
 * 엑셀에서 복사한 행(클립보드 TSV)을 그리드 행 데이터로 바꾼다. 그리드 메타만 보고 동작하는 공용 해석기.
 *   - 컬럼 순서 : 화면에 보이는 컬럼(fg_visible='Y')을 no_seq 순으로 왼쪽부터 대응
 *   - 앞쪽 남는 열 : 엑셀의 숨긴 열 등이 앞에 딸려 와도, 날짜·숫자 컬럼이 가장 잘 맞는 위치로 자동 정렬
 *   - 머리글 행 : 첫 행이 컬럼명과 같거나, 날짜·숫자 칸이 하나도 맞지 않으면 건너뜀
 *   - 값 변환 : SnDateBox → YYYYMMDD, SnDateMonthBox → YYYYMM (YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD 허용)
 *               SnNumberBox → 쉼표 제거 후 숫자(빈 칸 0), 나머지 → 앞뒤 공백 제거
 * 붙여넣은 행을 그리드에 어떻게 넣을지(이어 붙이기/교체)는 화면이 정한다. 키 입력 처리는 useGridPaste.
 */
// ponytail: 셀 안 줄바꿈(엑셀이 "..."로 감싼 값)은 처리 안 함 — 필요해지면 따옴표 파서로 교체.

export interface GridPasteError {
  /** 붙여넣은 데이터 기준 행 번호(머리글 제외, 1부터) */
  line: number;
  cd_column: string;
  nm_column: string;
  value: string;
}

export interface GridPasteResult {
  rows: Record<string, any>[];
  /** 날짜·숫자로 읽지 못한 칸. 값은 원문 그대로 행에 남긴다. */
  errors: GridPasteError[];
  /** 첫 행을 머리글로 보고 건너뛰었는지. 결과가 0건일 때 안내 문구를 가르는 데 쓴다. */
  headerSkipped: boolean;
}

type Kind = "date" | "month" | "number" | "text";

const kindOf = (col: ColumnConfig): Kind => {
  switch (col.tp_column) {
    case "SnDateBox":
      return "date";
    case "SnDateMonthBox":
      return "month";
    case "SnNumberBox":
      return "number";
    default:
      return "text";
  }
};

const validYmd = (y: number, m: number, d: number) => {
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

/** 날짜 문자열 → YYYYMMDD. 날짜가 아니면 null. */
export const toYmd = (v: any): string | null => {
  const s = String(v ?? "").trim();
  const m =
    s.match(/^(\d{4})(\d{2})(\d{2})$/) ??
    s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (!m || !validYmd(+m[1], +m[2], +m[3])) return null;
  return `${m[1]}${m[2].padStart(2, "0")}${m[3].padStart(2, "0")}`;
};

/** 연월 문자열 → YYYYMM. 날짜가 들어오면 연월만 쓴다. 아니면 null. */
export const toYm = (v: any): string | null => {
  const ymd = toYmd(v);
  if (ymd) return ymd.slice(0, 6);
  const m = String(v ?? "").trim().match(/^(\d{4})[-/.]?(\d{1,2})$/);
  if (!m || +m[2] < 1 || +m[2] > 12) return null;
  return `${m[1]}${m[2].padStart(2, "0")}`;
};

/** 숫자 문자열("1,234,000") → number. 숫자가 아니면 null. */
export const toNumber = (v: any): number | null => {
  const s = String(v ?? "").replace(/,/g, "").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const convert = (kind: Kind, raw: string): { value: any; ok: boolean } => {
  if (kind === "text") return { value: raw, ok: true };
  if (raw === "") return { value: kind === "number" ? 0 : "", ok: true };
  const value = kind === "date" ? toYmd(raw) : kind === "month" ? toYm(raw) : toNumber(raw);
  return value === null ? { value: raw, ok: false } : { value, ok: true };
};

/** 날짜·숫자 컬럼 기준으로 offset 위치가 몇 칸 맞는지(빈 칸은 셈에서 뺌). */
const typedScore = (cells: string[], cols: ColumnConfig[], offset: number) => {
  let hit = 0;
  let miss = 0;
  cols.forEach((col, c) => {
    const kind = kindOf(col);
    const raw = (cells[c + offset] ?? "").trim();
    if (kind === "text" || raw === "") return;
    if (convert(kind, raw).ok) hit++;
    else miss++;
  });
  return { hit, miss };
};

const norm = (s: any) => String(s ?? "").replace(/\s/g, "");

export const parseGridPaste = (
  text: string,
  columns: ColumnConfig[],
  maxLeadingSkip = 3
): GridPasteResult => {
  const cols = columns
    .filter((c) => String(c.fg_visible ?? "Y").trim() === "Y")
    .sort((a, b) => (a.no_seq ?? 0) - (b.no_seq ?? 0));

  let lines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.split("\t"))
    .filter((cells) => cells.some((c) => c.trim() !== ""));
  if (!lines.length || !cols.length)
    return { rows: [], errors: [], headerSkipped: false };

  // 머리글 1: 컬럼명이 그대로 있으면 머리글.
  const names = new Set(cols.map((c) => norm(c.nm_column)));
  const isNameHeader = lines[0].some((c) => c.trim() !== "" && names.has(norm(c)));

  // 앞쪽 남는 열: 머리글 후보를 뺀 앞 몇 행으로, 날짜·숫자가 가장 많이 맞고 덜 틀리는 위치를 고른다.
  const sample = lines.slice(isNameHeader || lines.length > 1 ? 1 : 0, 6);
  let offset = 0;
  let best = { hit: -1, miss: 0 };
  for (let k = 0; k <= maxLeadingSkip; k++) {
    const s = sample.reduce(
      (acc, cells) => {
        const r = typedScore(cells, cols, k);
        return { hit: acc.hit + r.hit, miss: acc.miss + r.miss };
      },
      { hit: 0, miss: 0 }
    );
    if (s.hit - s.miss > best.hit - best.miss) {
      best = s;
      offset = k;
    }
  }

  // 머리글 2: 이름이 달라도 날짜·숫자 칸이 하나도 안 맞으면(전부 글자) 머리글.
  const first = typedScore(lines[0], cols, offset);
  const headerSkipped =
    isNameHeader || (lines.length > 1 && first.hit === 0 && first.miss > 0);
  if (headerSkipped) {
    lines = lines.slice(1);
  }

  const errors: GridPasteError[] = [];
  const rows = lines.map((cells, i) => {
    const row: Record<string, any> = {};
    cols.forEach((col, c) => {
      const raw = (cells[c + offset] ?? "").trim();
      const { value, ok } = convert(kindOf(col), raw);
      row[col.cd_column] = value;
      if (!ok) {
        errors.push({
          line: i + 1,
          cd_column: col.cd_column,
          nm_column: col.nm_column,
          value: raw,
        });
      }
    });
    return row;
  });
  return { rows, errors, headerSkipped };
};
